import { useCallback, useState } from 'react';
import { useRecorder } from '../hooks/useRecorder';
import { useAuth } from '../hooks/AuthContext';
import { uploadAudio } from '../services/audioStorage';

const MAX_SECONDS = 15;

interface RecordButtonProps {
  onRecorded: (data: { noteId: string; storagePath: string; durationMs: number }) => void;
}

export function RecordButton({ onRecorded }: RecordButtonProps) {
  const { state, elapsedMs, start, stop, result, error, clear } = useRecorder();
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const progress = Math.min(elapsedMs / (MAX_SECONDS * 1000), 1);
  const seconds = Math.min(Math.floor(elapsedMs / 1000), MAX_SECONDS);

  const handleUpload = useCallback(async () => {
    if (!result || !user) return;
    setUploading(true);
    setUploadError(null);
    try {
      const noteId = crypto.randomUUID();
      const { storagePath } = await uploadAudio(user.id, noteId, result.blob);
      onRecorded({ noteId, storagePath, durationMs: result.durationMs });
      clear();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [result, user, onRecorded, clear]);

  const displayError = error || uploadError;

  return (
    <div style={styles.wrapper}>
      {/* Progress ring */}
      <div style={styles.ringContainer}>
        <svg style={styles.ringSvg} viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke="var(--color-border)"
            strokeWidth="3"
          />
          {state === 'recording' && (
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 54}`}
              strokeDashoffset={`${2 * Math.PI * 54 * (1 - progress)}`}
              style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dashoffset 100ms linear' }}
            />
          )}
        </svg>

        {/* Center button */}
        {state === 'idle' && !result && (
          <button
            style={styles.recordBtn}
            onClick={start}
            type="button"
            aria-label="Start recording"
          >
            <div style={styles.micIcon}>
              <MicSvg />
            </div>
          </button>
        )}

        {state === 'recording' && (
          <button
            style={{ ...styles.recordBtn, ...styles.recordBtnActive }}
            onClick={stop}
            type="button"
            aria-label="Stop recording"
          >
            <div style={styles.stopSquare} />
          </button>
        )}

        {state === 'stopped' && result && (
          <button
            style={styles.recordBtn}
            onClick={uploading ? undefined : handleUpload}
            type="button"
            aria-label="Save recording"
            disabled={uploading}
          >
            {uploading ? (
              <div style={styles.spinner} />
            ) : (
              <CheckSvg />
            )}
          </button>
        )}
      </div>

      {/* Timer label */}
      <p style={styles.timerLabel}>
        {state === 'recording' && `${seconds}s / ${MAX_SECONDS}s`}
        {state === 'stopped' && result && !uploading && 'Tap to save'}
        {uploading && 'Uploading...'}
        {state === 'idle' && !result && !displayError && 'Tap to record'}
      </p>

      {/* Discard button when stopped */}
      {state === 'stopped' && result && !uploading && (
        <button style={styles.discardBtn} onClick={clear} type="button">
          Discard
        </button>
      )}

      {/* Error */}
      {displayError && <p style={styles.error}>{displayError}</p>}
    </div>
  );
}

function MicSvg() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="1" width="6" height="12" rx="3" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function CheckSvg() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 'var(--space-3)',
  },
  ringContainer: {
    position: 'relative' as const,
    width: '100px',
    height: '100px',
  },
  ringSvg: {
    position: 'absolute' as const,
    inset: 0,
    width: '100%',
    height: '100%',
  },
  recordBtn: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '68px',
    height: '68px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-surface-elevated)',
    border: '2px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-primary)',
    transition: 'background-color var(--transition-fast), border-color var(--transition-fast), transform var(--transition-fast)',
  },
  recordBtnActive: {
    backgroundColor: 'var(--color-danger-light)',
    borderColor: 'var(--color-danger)',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  micIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stopSquare: {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    backgroundColor: 'var(--color-danger)',
  },
  spinner: {
    width: '24px',
    height: '24px',
    border: '2px solid var(--color-border)',
    borderTopColor: 'var(--color-accent)',
    borderRadius: '50%',
    animation: 'spin 0.6s linear infinite',
  },
  timerLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    textAlign: 'center' as const,
    minHeight: '1.25em',
  },
  discardBtn: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    padding: '4px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
  },
  error: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-danger)',
    textAlign: 'center' as const,
  },
};
